import React, { useEffect, useMemo, useRef, useState } from "react";
import Select, { components } from "react-select";
import api from "../../api";
import "./FormAssignmentUserSelector.css";

// Phase 4C, §15 (redesign). Searchable multi-select of assignment-eligible
// active users, sourced from GET /form-assignment-options/users -- the
// backend already scopes this list to the assigner's own resolved
// form_assignments:create data_scope (SELF/TEAM/DEPARTMENT/UNIT/ALL), never
// by role name (see services/formAssignmentOptionService.js). This
// component only presents that already-scoped list; it performs no
// additional client-side authorization filtering of its own.
const PAGE_SIZE = 500; // "reasonable employee volume" -- load the full eligible list in one call (server still supports search on top)

function initialOf(name) {
  const trimmed = String(name || "").trim();
  return trimmed ? trimmed[0].toUpperCase() : "?";
}

function toOption(u) {
  return {
    value: String(u.id),
    label: u.name || `User #${u.id}`,
    user: u,
    department: u.department_name || "Other",
  };
}

const Option = (props) => {
  const { data, isSelected } = props;
  const u = data.user;
  return (
    <components.Option {...props}>
      <div className="fau-option">
        <input type="checkbox" readOnly checked={isSelected} className="fau-option-checkbox" />
        <div className="fau-option-avatar">{initialOf(u.name)}</div>
        <div className="fau-option-lines">
          <div className="fau-option-name">{u.name || `User #${u.id}`}</div>
          <div className="fau-option-line2">
            {[u.employee_code ? `EMP-${String(u.employee_code).replace(/^EMP-/i, "")}` : null, u.role_name].filter(Boolean).join(" • ") || "—"}
          </div>
          <div className="fau-option-line3">
            {[u.designation_name, u.department_name].filter(Boolean).join(" • ") || "—"}
          </div>
          {u.unit_name ? <div className="fau-option-line4">{u.unit_name}</div> : null}
        </div>
      </div>
    </components.Option>
  );
};

function MenuList(props) {
  const { selectProps } = props;
  const { fauTotal, fauOnSelectAllVisible, fauVisibleCount } = selectProps;
  return (
    <>
      <div className="fau-menu-header">
        <span>Eligible Employees</span>
        <span className="fau-menu-count">{fauTotal}</span>
      </div>
      {fauVisibleCount > 0 && (
        <div className="fau-menu-actions">
          <button type="button" className="fau-select-all-btn" onMouseDown={(e) => { e.preventDefault(); fauOnSelectAllVisible(); }}>
            Select All Visible ({fauVisibleCount})
          </button>
        </div>
      )}
      <components.MenuList {...props}>{props.children}</components.MenuList>
    </>
  );
}

export default function FormAssignmentUserSelector({ value = [], onChange, isDisabled = false }) {
  const [rawUsers, setRawUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const requestSeq = useRef(0);

  const fetchUsers = async (searchText) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/form-assignment-options/users", { params: { search: searchText || undefined, pageSize: PAGE_SIZE } });
      if (seq !== requestSeq.current) return;
      setRawUsers(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.error("Fetch assignment-eligible users error:", err);
      setError(err.response?.data?.message || "Failed to load eligible employees.");
      setRawUsers([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchUsers(search), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Selected users not present in the current search page (search narrows
  // the fetched list) are preserved by name/details from a running cache,
  // so switching search text never silently drops a prior pick from the
  // "selected" summary panel or from what gets submitted.
  // A ref would not trigger a re-render when populated, leaving the
  // selected-panel one render behind (still showing "User #N" after the
  // real data arrives) -- state ensures the panel re-renders with the full
  // name/details as soon as they're known.
  const [knownUsers, setKnownUsers] = useState(new Map());
  useEffect(() => {
    if (!rawUsers.length) return;
    setKnownUsers((prev) => {
      const next = new Map(prev);
      let changed = false;
      rawUsers.forEach((u) => {
        const key = String(u.id);
        if (next.get(key) !== u) {
          next.set(key, u);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [rawUsers]);

  const allOptions = useMemo(() => rawUsers.map(toOption), [rawUsers]);

  const groupedOptions = useMemo(() => {
    const byDept = new Map();
    allOptions.forEach((opt) => {
      const key = opt.department || "Other";
      if (!byDept.has(key)) byDept.set(key, []);
      byDept.get(key).push(opt);
    });
    return [...byDept.entries()]
      .sort(([a], [b]) => (a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b)))
      .map(([label, options]) => ({ label, options }));
  }, [allOptions]);

  const selectedIds = useMemo(() => value.map(String), [value]);
  const selectedUsers = useMemo(
    () => selectedIds.map((id) => knownUsers.get(id) || { id, name: `User #${id}` }),
    [selectedIds, knownUsers]
  );

  const selectedOptionValues = useMemo(
    () => allOptions.filter((o) => selectedIds.includes(o.value)),
    [allOptions, selectedIds]
  );

  const handleSelectAllVisible = () => {
    // Only currently-filtered (visible/eligible) users -- never anyone
    // outside the already-scoped list this component received.
    const visibleIds = allOptions.map((o) => o.value);
    const merged = new Set([...selectedIds, ...visibleIds]);
    onChange([...merged].map(Number));
  };

  const handleClearAll = () => onChange([]);
  const handleRemoveOne = (id) => onChange(selectedIds.filter((v) => v !== String(id)).map(Number));

  const visibleShown = 3;
  const shownSelected = selectedUsers.slice(0, visibleShown);
  const extraCount = Math.max(0, selectedUsers.length - visibleShown);

  if (error) {
    return (
      <div className="fau-error-state">
        <span>⚠ {error}</span>
        <button type="button" className="fau-retry-btn" onClick={() => fetchUsers(search)}>Retry</button>
      </div>
    );
  }

  return (
    <div className="fau-wrap">
      <Select
        classNamePrefix="fau"
        isMulti
        isDisabled={isDisabled}
        isLoading={loading}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        backspaceRemovesValue={false}
        controlShouldRenderValue={false}
        menuIsOpen={menuOpen}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        options={groupedOptions}
        value={selectedOptionValues}
        onChange={(opts) => onChange((opts || []).map((o) => Number(o.value)))}
        onInputChange={(text, meta) => {
          if (meta.action === "input-change") setSearch(text);
          return text;
        }}
        components={{ Option, MenuList }}
        // Custom props consumed by our MenuList above -- react-select passes
        // any extra prop straight through to selectProps.
        fauTotal={allOptions.length}
        fauVisibleCount={allOptions.length}
        fauOnSelectAllVisible={handleSelectAllVisible}
        placeholder="Search by name, employee code, role, designation, department, unit..."
        noOptionsMessage={() =>
          loading
            ? "Loading employees..."
            : search
            ? "No eligible employees match your search."
            : "No eligible employees found in your permitted scope."
        }
      />

      <div className="fau-selected-panel">
        {selectedUsers.length === 0 ? (
          <span className="fau-selected-empty">No employees selected yet.</span>
        ) : (
          <>
            <div className="fau-selected-chips">
              {shownSelected.map((u) => (
                <span className="fau-chip" key={u.id}>
                  {u.name}
                  <button type="button" className="fau-chip-remove" onClick={() => handleRemoveOne(u.id)} aria-label={`Remove ${u.name}`}>×</button>
                </span>
              ))}
              {extraCount > 0 && <span className="fau-chip fau-chip-more">+{extraCount} more</span>}
            </div>
            <div className="fau-selected-footer">
              <span>{selectedUsers.length} employee{selectedUsers.length === 1 ? "" : "s"} selected</span>
              {selectedUsers.length > 1 && (
                <button type="button" className="fau-clear-all-btn" onClick={handleClearAll}>Clear All</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
